import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Quote, ArrowRight, Instagram } from "lucide-react";
import { Navbar } from "../components/Navbar";
import { Footer } from "../components/Footer";

const VoicesPage = () => {
  return (
    <div className="App bg-black text-white min-h-screen">
      <Navbar />

      {/* Header */}
      <section className="relative pt-32 md:pt-40 pb-12 border-b border-neutral-900 bg-gradient-to-b from-[#0a0a0a] to-black">
        <div className="max-w-[1440px] mx-auto px-6 md:px-12">
          <div className="flex items-center gap-3 text-[10px] uppercase tracking-[0.45em] text-amber-400 mb-6">
            <span className="w-8 h-px bg-amber-400" />
            <Quote size={14} />
            <span>Voices of the Movement</span>
          </div>
          <h1
            data-testid="voices-title"
            className="font-display uppercase text-5xl md:text-7xl lg:text-8xl leading-[0.88] text-white"
          >
            In their
            <br />
            <span className="text-amber-400">own words.</span>
          </h1>
          <p className="text-neutral-400 max-w-xl mt-6 font-light leading-relaxed">
            The athletes carrying AfroAm forward — their stories, their
            heritage, their reason for the game.
          </p>
        </div>
      </section>

      {/* Featured Quote */}
      <section className="py-16 md:py-24 bg-black">
        <div className="max-w-[1440px] mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
            {/* Quote text */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="lg:col-span-7 order-2 lg:order-1"
            >
              <Quote size={42} className="text-amber-400 mb-6 -rotate-180" />
              <blockquote
                data-testid="quote-block"
                className="font-display uppercase text-4xl md:text-5xl lg:text-6xl leading-[0.95] text-white"
              >
                It's important to share{" "}
                <span className="text-amber-400">cultures</span>{" "}
                <span className="text-green-500">in sports.</span>
                <br />
                <br />
                As a{" "}
                <span className="text-red-500">Samoan woman</span>{" "}
                by heritage,
                <br />I feel blessed to have{" "}
                <span className="text-amber-400">two cultures</span>{" "}
                <span className="text-green-500">
                  to share with our sons.
                </span>
              </blockquote>

              <div className="mt-10 pt-8 border-t border-neutral-900">
                <div className="text-[10px] uppercase tracking-[0.4em] text-amber-400 mb-2">
                  — The Athlete
                </div>
                <div className="font-display text-3xl text-white uppercase">
                  Felice Falu-Adams
                </div>
                <div className="text-sm text-neutral-400 uppercase tracking-[0.2em] mt-1">
                  Basketball · USA
                </div>
              </div>

              <div className="flex flex-wrap gap-3 mt-10">
                <Link
                  to="/family"
                  data-testid="voices-view-poster"
                  className="inline-flex items-center gap-2 bg-amber-400 text-black px-6 py-3.5 text-xs uppercase tracking-[0.28em] font-bold hover:bg-amber-300 transition-colors"
                >
                  View Team Poster
                  <ArrowRight size={14} />
                </Link>
                <a
                  href="https://instagram.com/afroamhoops"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="voices-instagram"
                  className="inline-flex items-center gap-2 border border-white text-white px-6 py-3.5 text-xs uppercase tracking-[0.28em] font-bold hover:bg-white hover:text-black transition-colors"
                >
                  <Instagram size={14} />
                  @afroamhoops
                </a>
              </div>
            </motion.div>

            {/* Poster image */}
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.9 }}
              className="lg:col-span-5 order-1 lg:order-2 relative"
            >
              <div className="relative border border-amber-400/30">
                <img
                  src="/posters/felice-quote.png"
                  alt="Felice Falu-Adams — It's important to share cultures in sports"
                  className="w-full h-auto object-contain"
                  data-testid="felice-poster"
                />
                <span className="absolute -top-2 -left-2 w-6 h-6 border-t-2 border-l-2 border-amber-400" />
                <span className="absolute -top-2 -right-2 w-6 h-6 border-t-2 border-r-2 border-amber-400" />
                <span className="absolute -bottom-2 -left-2 w-6 h-6 border-b-2 border-l-2 border-amber-400" />
                <span className="absolute -bottom-2 -right-2 w-6 h-6 border-b-2 border-r-2 border-amber-400" />
              </div>
              <div className="mt-4 flex items-center justify-between text-[10px] uppercase tracking-[0.35em] text-neutral-500">
                <span>Athlete Spotlight 01</span>
                <span>2026</span>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Upcoming voices teaser */}
      <section className="border-t border-neutral-900 py-16 bg-[#080808]">
        <div className="max-w-[1440px] mx-auto px-6 md:px-12 text-center">
          <div className="text-[10px] uppercase tracking-[0.4em] text-neutral-500 mb-4">
            — More Voices Coming Soon
          </div>
          <h3 className="font-display uppercase text-3xl md:text-5xl text-white max-w-3xl mx-auto leading-tight">
            Every athlete carries a continent. Every story expands the family.
          </h3>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default VoicesPage;
